import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useRevealButton } from '../../Hooks/use-reveal-button';
import { ChatAvatar } from '../chat-avatar/chat-avatar';
import { PlayMenu } from '../play-menu/play-menu';
import { RevealBtn } from '../revel-btn/revel-btn';
import clsx from 'clsx';
import { audioManager } from 'lib/audio/audio-manager';
import { AUDIO_TYPES } from 'lib/audio/audio.types';

import { useMoveHandler } from 'modules/Room/Hooks/useMoveHandler';
import { NULL_MOVE } from 'modules/Room/constants/constants';
import { CardPlayerEnum, LoadersStateEnum } from 'modules/Room/constants/enums';
import { NewFinishView } from 'modules/Room/game-status/components/game-finish-views/new-finish-view';
import { GameStatusEnum, MintingEnum } from 'modules/Room/game-status/constants/game-status-enum';
import { GameStatus } from 'modules/Room/game-status/game-statuses';
import { useGameStatusStore } from 'modules/Room/game-status/store/game-status-store';
import { Hearts } from 'modules/Room/mobile/mobile-game-card/hearts';

import AnimatedTransition from 'shared/components/ui/AnimatedTransition/AnimatedTransition';
import MintingLoader from 'shared/components/ui/Loaders/MintingLoader/MintingLoader';
import { SearchingForOpponent } from 'shared/components/ui/Loaders/SearchingForOpponent/SearchingForOpponent';
import { MoveStatusRenderer } from 'shared/components/ui/MoveStatusRenderer/MoveStatusRenderer';
import { MoveStatusType } from 'shared/components/ui/MoveStatusRenderer/MoveStatusRenderer.types';
import PrimaryButton from 'shared/components/ui/PrimaryButton/PrimaryButton';
import { InfiniteMaxProgressBar } from 'shared/components/ui/progress-bar/progress-bar';
import {
  DEFAULT_SLEEP_TIMEOUT,
  DEFAULT_Z_ICON_COUNT,
  NULL_ADDRESS,
  TOTAL_WINS_TO,
} from 'shared/constants/constants';
import { MovesEnum, RoomStatusEnum, WinnerStatusEnum } from 'shared/constants/shared-enums';
import {
  lookingForOpponent,
  madeMoveMessages,
  madeRevealMessages,
  takeMoneyBackMessages,
  technicalDefeatMessages,
  waitingToAcceptMessages,
  waitYourMoveMessages,
  waitYourRevealMessages,
} from 'shared/constants/text-loaders';
import { useAccount } from 'shared/hooks/use-web3';
import { useGlobalStore } from 'shared/store/globalStore';
import { useQuestsStore } from 'shared/store/questsStore';
import { useRoomStore } from 'shared/store/roomStore';
import { prettyTx } from 'shared/utils/pretty-tx';
import { getScannerLink } from 'shared/utils/scanner-link';

import ZIcon from 'assets/animation/sleep/Z.svg?react';

import styles from './player-card.module.css';

interface IPlayerCardProps {
  playerAddress: `0x${string}`;
  position: string;
  winsTo: number;
  onShareClick?: () => void;
  onRevengeClick?: () => void;
  onAcceptInvite?: (roomId: number) => void;
  invitedRoomId: number | null;
}

export const PlayerCard: FC<IPlayerCardProps> = ({
  playerAddress,
  position,
  winsTo,
  onShareClick,
  onRevengeClick,
  onAcceptInvite,
  invitedRoomId,
}) => {
  const { activeRoomId } = useParams();
  const [loadersState, setLoadersState] = useState<LoadersStateEnum>(LoadersStateEnum.None);
  const { pointBalance } = useQuestsStore();

  const { address, chainId } = useAccount();
  const leaderboardMap = useGlobalStore((state) => state.leaderboardMap);
  const {
    amIPlayerA,
    someoneElseGame,
    playerA,
    playerB,
    roomStatus,
    winnerStatus,
    roundMoves,
    roomExpire,
    expire,
  } = useRoomStore();

  const { gameStatus, mintingLoader, mintingHash } = useGameStatusStore();
  let sleepTimeoutRef = useRef<NodeJS.Timeout>();

  const amIHost = useMemo(
    () => playerAddress.toLowerCase() === (address?.toLowerCase() ?? ''),
    [playerAddress, address],
  );

  const isOpponentConnected = useMemo(() => playerB.address !== NULL_ADDRESS, [playerB.address]);
  const isRoomOpen = useMemo(() => roomStatus === RoomStatusEnum.Open, [roomStatus]);
  const isRoomClosed = useMemo(() => roomStatus === RoomStatusEnum.Closed, [roomStatus]);

  const { t } = useTranslation();

  const { handleSelectMove } = useMoveHandler();

  const playerName = useCallback(() => {
    if (someoneElseGame) {
      if (playerB.address === playerAddress) {
        return 'common.playerB';
      } else {
        return 'common.playerA';
      }
    } else if (amIHost) {
      return 'common.you';
    } else if (!activeRoomId && playerAddress === NULL_ADDRESS) {
      return 'common.noOpponent';
    } else if (playerAddress === NULL_ADDRESS) {
      return 'common.connecting';
    } else {
      return 'common.opponent';
    }
  }, [someoneElseGame, playerB.address, playerAddress, amIHost, activeRoomId]);

  const showAnimation = useMemo(() => {
    if (isRoomClosed) return false;
    if (mintingLoader !== MintingEnum.NONE) return true;
    if (!amIHost && loadersState === LoadersStateEnum.ChooseMove) return true;
    if (!amIHost && loadersState === LoadersStateEnum.Reveal) return true;
    if (amIHost && loadersState === LoadersStateEnum.WaitingToAccept) return true;
    if (!amIHost && loadersState === LoadersStateEnum.LookingOpponent) return true;
    if (
      !amIHost &&
      loadersState === LoadersStateEnum.LookingOpponent &&
      playerA.address === address?.toLowerCase()
    )
      return true;
    if (
      !amIHost &&
      (loadersState === LoadersStateEnum.MoveDone || loadersState === LoadersStateEnum.RevealDone)
    )
      return true;
    return false;
  }, [roomStatus, mintingLoader, amIHost, loadersState, playerA.address, address]);

  const renderAnimation = useCallback(() => {
    if (!amIHost && loadersState === LoadersStateEnum.ChooseMove)
      return (
        <MoveStatusRenderer
          statusIcon={MoveStatusType.WAITING_FOR_OPPONENT}
          title={t('common.pickingHand')}
          className="w-full p-4 hd:p-0"
        />
      );

    if (!amIHost && loadersState === LoadersStateEnum.Reveal)
      return (
        <MoveStatusRenderer
          statusIcon={MoveStatusType.WAITING_FOR_OPPONENT}
          title={t('common.revealing')}
          className="w-full p-4 hd:p-0"
        />
      );
    if (
      !amIHost &&
      (loadersState === LoadersStateEnum.MoveDone || loadersState === LoadersStateEnum.RevealDone)
    )
      return (
        <MoveStatusRenderer
          statusIcon={MoveStatusType.WAITING_FOR_OPPONENT}
          title={
            loadersState === LoadersStateEnum.MoveDone ? t(madeMoveMessages) : t(madeRevealMessages)
          }
          className="w-full p-4 hd:p-0"
        />
      );

    if (
      isRoomOpen &&
      playerA.address === (address?.toLowerCase() ?? '') &&
      gameStatus !== GameStatusEnum.NO_ONE_JOIN
    ) {
      return (
        <SearchingForOpponent
          className="mx-auto w-full"
          messages={isOpponentConnected ? waitingToAcceptMessages : lookingForOpponent}
          waitingToAccept={isOpponentConnected}
        />
      );
    }

    if (amIHost && mintingLoader === MintingEnum.TECHNICAL_DEFEAT) {
      return <MintingLoader hash={mintingHash} messages={technicalDefeatMessages} />;
    }
    if (amIHost && mintingLoader === MintingEnum.YOUR_MOVE) {
      return (
        <MintingLoader
          className="mt-10 flex items-center justify-center px-5 hd:mt-0"
          hash={mintingHash}
          messages={waitYourMoveMessages}
        />
      );
    }
    if (amIHost && mintingLoader === MintingEnum.YOUR_REVEAL) {
      return (
        <MintingLoader
          className="mt-10 flex items-center justify-center px-5 hd:mt-0"
          hash={mintingHash}
          messages={waitYourRevealMessages}
        />
      );
    }
    if (amIHost && mintingLoader === MintingEnum.TAKE_MONEY_BACK) {
      return (
        <MintingLoader
          className="mt-10 flex items-center justify-center px-5 xlg:mt-0"
          hash={mintingHash}
          messages={takeMoneyBackMessages}
        />
      );
    }
  }, [amIHost, loadersState, mintingLoader, mintingHash]);

  const renderGameResultImage = useCallback(() => {
    if (roomStatus !== RoomStatusEnum.Closed || winnerStatus === WinnerStatusEnum.PlayerAExitRoom)
      return null;
    if (amIHost) {
      return (
        <NewFinishView
          isWinner={
            (amIPlayerA &&
              (winnerStatus === WinnerStatusEnum.WinnerPlayerA ||
                winnerStatus === WinnerStatusEnum.TechnicalWinnerA)) ||
            (!amIPlayerA &&
              (winnerStatus === WinnerStatusEnum.WinnerPlayerB ||
                winnerStatus === WinnerStatusEnum.TechnicalWinnerB))
          }
          amIWinner={
            (amIPlayerA &&
              (winnerStatus === WinnerStatusEnum.WinnerPlayerA ||
                winnerStatus === WinnerStatusEnum.TechnicalWinnerA)) ||
            (!amIPlayerA &&
              (winnerStatus === WinnerStatusEnum.WinnerPlayerB ||
                winnerStatus === WinnerStatusEnum.TechnicalWinnerB))
          }
          showModal
          onShareClick={onShareClick}
          winnerStatus={winnerStatus}
          onRevengeClick={onRevengeClick}
          onAcceptInvite={onAcceptInvite}
          invitedRoomId={invitedRoomId}
        />
      );
    } else {
      return (
        <NewFinishView
          isWinner={
            (amIPlayerA &&
              (winnerStatus === WinnerStatusEnum.WinnerPlayerB ||
                winnerStatus === WinnerStatusEnum.TechnicalWinnerB)) ||
            (!amIPlayerA &&
              (winnerStatus === WinnerStatusEnum.WinnerPlayerA ||
                winnerStatus === WinnerStatusEnum.TechnicalWinnerA))
          }
          onShareClick={onShareClick}
          onRevengeClick={onRevengeClick}
          onAcceptInvite={onAcceptInvite}
          invitedRoomId={invitedRoomId}
        />
      );
    }
  }, [amIHost, amIPlayerA, winnerStatus, onShareClick, roomStatus]);

  const getRemainingWins = useCallback(() => {
    let wins = winsTo ?? TOTAL_WINS_TO;
    if (someoneElseGame) {
      if (playerName() === 'common.playerA') {
        return wins - Number(playerB.wins);
      } else if (playerName() === 'common.playerB' && isOpponentConnected) {
        return wins - Number(playerA.wins);
      }
    } else if (amIHost) {
      return wins - Number(amIPlayerA ? playerB.wins : playerA.wins);
    } else if (activeRoomId && isOpponentConnected) {
      return wins - Number(amIPlayerA ? playerA.wins : playerB.wins);
    }
    return null;
  }, [winsTo, someoneElseGame, playerName, playerB, playerA, amIHost, amIPlayerA, activeRoomId]);

  const remainingWins = getRemainingWins();

  const { showCloseButton, isSuccess, closeUnusedRoomHandler } = useRevealButton('waitYourReveal');

  const isRevealButtonVisible = useMemo(
    () =>
      gameStatus === GameStatusEnum.WAIT_YOUR_REVEAL &&
      amIHost &&
      !showCloseButton &&
      !expire &&
      !roomExpire &&
      roomStatus === RoomStatusEnum.ReadyForReveal &&
      mintingLoader !== MintingEnum.YOUR_REVEAL,
    [gameStatus, amIHost, showCloseButton, mintingLoader, roomStatus, expire, roomExpire],
  );

  const isTechnicalDefeatButtonVisible = useMemo(
    () => (roomExpire || expire) && !isSuccess && amIHost && roomStatus !== RoomStatusEnum.Closed,
    [roomExpire, expire, isSuccess, amIHost, roomStatus, gameStatus, mintingLoader],
  );

  useEffect(() => {
    const clearCurrentTimeout = () => {
      if (sleepTimeoutRef.current) {
        clearTimeout(sleepTimeoutRef.current);
      }
    };

    const setPlayerLoaderState = (opponentMove: `0x${string}`) => {
      if (opponentMove === NULL_MOVE) {
        setLoadersState(LoadersStateEnum.ChooseMove);
      } else {
        setLoadersState(LoadersStateEnum.MoveDone);
      }
    };

    const setRevealLoaderState = (opponentEncrMove: MovesEnum) => {
      if (opponentEncrMove === MovesEnum.None) {
        setLoadersState(LoadersStateEnum.Reveal);
      } else {
        setLoadersState(LoadersStateEnum.RevealDone);
      }
    };

    clearCurrentTimeout();

    if (
      gameStatus === GameStatusEnum.WAIT_YOUR_MOVE ||
      gameStatus === GameStatusEnum.WAIT_YOUR_APPROVE
    ) {
      if (amIPlayerA) {
        setPlayerLoaderState(roundMoves.playerB.encrMove);
      } else {
        setPlayerLoaderState(roundMoves.playerA.encrMove);
      }
    } else if (
      gameStatus === GameStatusEnum.WAIT_OPPONENT_MOVE ||
      gameStatus === GameStatusEnum.WAIT_OPPONENT_REVEAL
    ) {
      setLoadersState(LoadersStateEnum.None);
      sleepTimeoutRef.current = setTimeout(() => {
        setLoadersState(LoadersStateEnum.Sleep);
      }, DEFAULT_SLEEP_TIMEOUT);
    } else if (gameStatus === GameStatusEnum.WAIT_YOUR_REVEAL) {
      if (amIPlayerA) {
        setRevealLoaderState(roundMoves.playerB.move);
      } else {
        setRevealLoaderState(roundMoves.playerA.move);
      }
    } else if (!amIHost && isRoomOpen) {
      if (isOpponentConnected) {
        setLoadersState(LoadersStateEnum.WaitingToAccept);
      }
    } else {
      setLoadersState(LoadersStateEnum.None);
    }
    return clearCurrentTimeout;
  }, [gameStatus, amIPlayerA, roundMoves, roomStatus, amIHost]);

  useEffect(() => {
    if (mintingLoader === MintingEnum.YOUR_MOVE || mintingLoader === MintingEnum.YOUR_REVEAL) {
      audioManager.playSound(AUDIO_TYPES.CONFIRM_MOVE);
    }
  }, [mintingLoader]);

  useEffect(() => {
    if (isRoomOpen && playerA.address === address?.toLowerCase()) {
      if (gameStatus !== GameStatusEnum.NO_ONE_JOIN) {
        setLoadersState(LoadersStateEnum.LookingOpponent);
      }
    } else if (gameStatus === GameStatusEnum.JOIN_GAME) {
      setLoadersState(LoadersStateEnum.WaitingToAccept);
    } else if (roomStatus === RoomStatusEnum.None) {
      setLoadersState(LoadersStateEnum.None);
    }
  }, [gameStatus, roomStatus, playerA.address, address]);
  const screenAddress = useMemo(
    () =>
      prettyTx({
        address: playerAddress,
        leaderboardMap,
        startAddressLength: 4,
        endAddressLength: 3,
      }),
    [playerAddress, leaderboardMap],
  );
  return (
    <div
      className="watch-game-card relative flex h-full w-full flex-col items-center rounded-[22px] border border-border bg-background bg-opacity-30 pt-2.5 backdrop-blur-xl transition"
      style={{
        background: 'rgba(5, 4, 57, 0.6)',
      }}
    >
      <div className="flex flex-col items-center">
        <div className="mb-[10px] flex flex-row">
          {playerAddress !== NULL_ADDRESS ? (
            <ChatAvatar
              key={playerAddress}
              userAddress={playerAddress}
              amIHost={amIHost}
              isGameInProgress={playerA.address !== NULL_ADDRESS && isOpponentConnected}
              className="h-[64px] w-[64px] sm:h-[97px] sm:w-[97px]"
            />
          ) : (
            <div className="flex h-[97px] w-[97px] items-center justify-center rounded-[12px] bg-deep-blue">
              <span className="text-[58px] text-blue">?</span>
            </div>
          )}
        </div>
        <AnimatedTransition
          show={
            !amIHost &&
            loadersState === LoadersStateEnum.Sleep &&
            roomStatus !== RoomStatusEnum.Closed
          }
        >
          <div className="absolute left-1/2 top-4 z-10 -translate-x-1/2 transform">
            {Array.from({ length: DEFAULT_Z_ICON_COUNT }).map((_, index) => (
              <ZIcon key={index} className={styles.zIcon} />
            ))}
          </div>
        </AnimatedTransition>

        <div className="rounded-lg border border-border bg-dark-background px-8 py-[3px]">
          {chainId ? (
            <a
              className="cursor-pointer text-[20px] text-yellow"
              style={{ opacity: playerAddress === NULL_ADDRESS ? 0 : 1 }}
              href={getScannerLink(playerAddress, chainId.toString())}
              target="_blank"
              rel="noreferrer"
            >
              {screenAddress}
            </a>
          ) : (
            <span
              className="text-[20px] text-yellow"
              style={{ opacity: playerAddress === NULL_ADDRESS ? 0 : 1 }}
            >
              {screenAddress}
            </span>
          )}
        </div>

        <div className="rounded-bl-lg rounded-br-lg rounded-tl-none rounded-tr-none border border-t-0 border-border bg-dark-background px-[32px] py-[3px]">
          <span className="text-[14px] text-gray">{t(playerName())}</span>
        </div>
        <div
          className={clsx('mt-4 flex gap-4 text-white', {
            'animate-fast-appear': remainingWins !== null,
            'scale-x-[-1] transform': position === 'order-3',
            hidden: isRoomClosed,
          })}
        >
          <Hearts
            wins={winsTo - remainingWins}
            winsTo={Number(winsTo)}
            opponent={!amIHost}
            prefix={amIHost ? 'a' : 'b'}
            heartsSize="md:h-10 md:w-11 hd:h-[60px] hd:w-[66px]"
          />
        </div>
        {roomStatus !== RoomStatusEnum.Closed && position === 'order-1' && (
          <InfiniteMaxProgressBar
            value={Number(pointBalance)}
            showAttention
            className="absolute bottom-20 left-1/2 z-50 w-[90%] max-w-72 -translate-x-1/2 sm:bottom-[-1rem]"
          />
        )}

        {renderGameResultImage()}
      </div>

      <AnimatedTransition
        show={showAnimation}
        className={`flex flex-grow items-center justify-center ${showAnimation ? 'flex' : 'hidden'}`}
      >
        {renderAnimation()}
      </AnimatedTransition>
      <GameStatus
        player={
          amIHost
            ? CardPlayerEnum.Player
            : playerAddress === NULL_ADDRESS
              ? CardPlayerEnum.Connect
              : CardPlayerEnum.Opponent
        }
        isShowEndGameAnimation={
          (winnerStatus !== WinnerStatusEnum.None &&
            winnerStatus !== WinnerStatusEnum.PlayerAExitRoom) ||
          mintingLoader !== MintingEnum.NONE
        }
      />

      {isRevealButtonVisible && (
        <div className="flex w-full flex-col items-center justify-center px-5">
          <RevealBtn className="min-w-[10rem]" />
        </div>
      )}

      {isTechnicalDefeatButtonVisible && (
        <div className="flex w-full flex-col items-center justify-center px-5 pb-5">
          <PrimaryButton text="common.technicalDefeat" onClick={closeUnusedRoomHandler} />
        </div>
      )}
      {amIHost &&
        isOpponentConnected &&
        roomStatus !== RoomStatusEnum.Open &&
        !roomExpire &&
        roomStatus !== RoomStatusEnum.Closed && (
          <div className={`mt-auto flex w-full items-center justify-around self-end`}>
            <PlayMenu selectMove={handleSelectMove} />
          </div>
        )}
      {!amIHost &&
        isOpponentConnected &&
        roomStatus !== RoomStatusEnum.Open &&
        !roomExpire &&
        roomStatus !== RoomStatusEnum.Closed && (
          <div className="h-[13.125rem] md:h-[15.25rem] hd:h-[15.625rem]" />
        )}
    </div>
  );
};
